"use client"

import Image from "next/image"
import Link from "next/link"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"
import type { PageSection } from "~/services/triibo-service"

interface HeroSectionProps {
  data: PageSection
}

const CAROUSEL_AUTOPLAY_INTERVAL = 4000

export const HeroSection = ({ data }: HeroSectionProps) => {
  const slides = data?.slides ?? []

  if (slides.length === 0) {
    return null
  }

  return (
    <section aria-label='Carrossel do banner do hero' className='w-full'>
      <div className='relative w-full overflow-hidden rounded-lg'>
        <Carousel
          className='w-full'
          opts={{
            loop: true,
            duration: CAROUSEL_AUTOPLAY_INTERVAL,
          }}
        >
          <CarouselContent>
            {slides.map((slide, index) => (
              <CarouselItem
                key={`slide-${
                  // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                  index
                }`}
              >
                {slide.image?.url && (
                  <Link
                    className='block h-full w-full'
                    href={slide.link?.url || "#"}
                    rel={
                      slide.link?.target === "_blank" ? "noopener" : undefined
                    }
                    target={slide.link?.target || "_self"}
                  >
                    <picture>
                      <source
                        media='(min-width: 768px)'
                        srcSet={
                          slide.image.sizes?.["1536x1536"] || slide.image.url
                        }
                      />
                      <Image
                        alt={slide.image.alt || `Hero slide ${index + 1}`}
                        className='h-auto w-full'
                        height={slide.image.height}
                        priority={index === 0}
                        sizes='100vw'
                        src={
                          slide.imageMobile?.sizes?.medium ||
                          slide.imageMobile?.url ||
                          slide.image.url
                        }
                        width={slide.image.width}
                      />
                    </picture>
                  </Link>
                )}
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious
            aria-label='Slide anterior'
            className='absolute top-1/2 left-4 -translate-y-1/2'
          />
          <CarouselNext
            aria-label='Próximo slide'
            className='absolute top-1/2 right-4 -translate-y-1/2'
          />
        </Carousel>

        {/* Schema.org structured data */}
        <script suppressHydrationWarning type='application/ld+json'>
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ImageGallery",
            name: "Hero Banner Slides",
            image: slides
              .filter((slide) => slide.image?.url)
              .map((slide) => ({
                "@type": "ImageObject",
                contentUrl: slide.image.url,
                description: slide.image.alt || "",
              })),
          })}
        </script>
      </div>
    </section>
  )
}
